import {LogOut, LogIn, User, HelpCircle, Plus, Compass, Heart, ClipboardList, Route, Search, UserPlus} from 'lucide-react';
import {useMsal} from '@azure/msal-react';
import {Link, useLocation} from '@tanstack/react-router';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LanguageSwitch
} from "@city-sage/aurum-avis-labs-components";
import { useAuth } from "@city-sage/aurum-avis-labs-components";
import { useTranslation } from 'react-i18next';
import { useScrollDirection } from '../../lib/hooks/use-scroll-direction';
import { cn } from '@city-sage/aurum-avis-labs-components';

// Types
interface NavItem {
  href: string;
  icon: React.ReactNode;
  label: string;
}

// Mobile bottom nav items
const mobileNavItems = [
  { icon: Compass, label: "Explore", href: "/" },
  { icon: Route, label: "My Itineraries", href: "/my-itineraries" },
  { icon: Plus, label: "Create", href: "/itineraries/create" },
  { icon: Search, label: "Search", href: "/search" },
  { icon: User, label: "Profile", href: "/profile" },
];

// Components
const Logo = () => (
  <Link to="/" className="flex-shrink-0">
    <img
      src={window.location.origin + '/logo_only.webp'}
      alt="CitySage Logo"
      className="h-9 w-auto"
    />
  </Link>
);

const MobileNavbar = ({
  isAuthenticated,
  handleLogin,
  handleSignUp,
  handleLogout
}: {
  isAuthenticated: boolean;
  handleLogin: () => void;
  handleSignUp: () => void;
  handleLogout: () => void;
}) => {
  const { t } = useTranslation();
  const { userEmail, userName } = useAuth();

  return (
    <nav className="block md:hidden z-50 bg-white fixed top-0 w-full border-b border-gray-100">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left side - Logo with name */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src={window.location.origin + '/logo_only.webp'}
              alt="CitySage Logo"
              className="h-6 w-auto"
            />
            <span className="text-base font-semibold text-gray-900">CitySage</span>
          </Link>

          {/* Right side - Action buttons */}
          <div className="flex items-center gap-2">
            <Link to="/itineraries/create">
              <Button size="sm" variant='outline' className="h-8 px-3">
                <Plus className="h-4 w-4 mr-1" />
                <span className="text-xs">Create</span>
              </Button>
            </Link>
            
            {/* Auth Buttons */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <Link to="/profile">
                    <DropdownMenuItem className="font-normal">
                      <div className="flex flex-col space-y-1 w-full">
                        <p className="text-sm font-medium leading-none">
                          {userName}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground text-ellipsis overflow-hidden pb-1">
                          {userEmail}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  </Link>
                  <DropdownMenuSeparator/>
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4"/>
                    <span>{t('common.buttons.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-1">
                <Button onClick={handleSignUp} variant="default" size="sm" className="h-8 px-2">
                  <UserPlus className="h-4 w-4 mr-1" />
                  <span className="text-xs">Sign Up</span>
                </Button>
                <Button onClick={handleLogin} variant="ghost" size="sm" className="h-8 px-2">
                  <LogIn className="h-4 w-4 mr-1" />
                  <span className="text-xs">{t('common.buttons.login')}</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const MobileBottomNav = () => {
  const location = useLocation();

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 block md:hidden">
      <div className="flex">
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;

          return (
            <Link
              key={item.label}
              to={item.href}
              className="flex-1 flex items-center justify-center py-2 px-1"
            >
              <Icon className={cn("h-5 w-5", isActive ? "text-primary" : "text-gray-400")} />
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

const DesktopSidebar = ({
  isAuthenticated,
  handleLogin,
  handleSignUp,
  handleLogout
}: {
  isAuthenticated: boolean;
  handleLogin: () => void;
  handleSignUp: () => void;
  handleLogout: () => void;
}) => {
  const { t } = useTranslation();
  const { userEmail, userName } = useAuth();
  const location = useLocation();

  const sidebarNavItems = [
    { icon: Compass, label: t('navigation.explore', 'Explore'), href: "/" },
    { icon: Route, label: t('navigation.myItineraries', 'My Itineraries'), href: "/my-itineraries" },
    { icon: Plus, label: t('navigation.create', 'Create'), href: "/itineraries/create" },
    { icon: Search, label: t('navigation.search', 'Search'), href: "/search" },
    { icon: HelpCircle, label: t('common.buttons.help'), href: "/help" },
  ];

  return (
    <aside className="fixed left-0 top-0 h-full w-16 bg-background border-r border-border z-40 hidden md:flex flex-col shadow-sm">
      {/* Logo */}
      <div className="flex items-center justify-center py-4 border-b border-border">
        <Link to="/" className="flex-shrink-0">
          <img
            src={window.location.origin + '/logo_only.webp'}
            alt="CitySage Logo"
            className="h-8 w-8 rounded-lg"
          />
        </Link>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 flex flex-col items-center py-4 space-y-2">
        {sidebarNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;

          return (
            <Link
              key={item.label}
              to={item.href}
              className={cn(
                "relative group flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200",
                isActive 
                  ? "bg-primary text-primary-foreground shadow-lg" 
                  : "text-muted-foreground hover:text-foreground hover:bg-accent"
              )}
            >
              <Icon className="h-6 w-6" />
              
              {/* Tooltip */}
              <div className="absolute left-16 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                {item.label}
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="border-t border-border p-3">
        {isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "relative group w-12 h-12 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-200",
                  location.pathname === "/profile" && "bg-primary text-primary-foreground"
                )}
              >
                <User className="h-6 w-6"/>
                
                {/* Tooltip */}
                <div className="absolute left-16 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  {userName || 'Profile'}
                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 ml-4" align="start" side="right">
              <Link to="/profile">
                <DropdownMenuItem className="font-normal">
                  <div className="flex flex-col space-y-1 w-full">
                    <p className="text-sm font-medium leading-none">
                      {userName}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground text-ellipsis overflow-hidden pb-1">
                      {userEmail}
                    </p>
                  </div>
                </DropdownMenuItem>
              </Link>
              <DropdownMenuSeparator/>
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4"/>
                <span>{t('common.buttons.logout')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="space-y-2">
            <Button
              onClick={handleSignUp}
              variant="default"
              size="icon"
              className="w-12 h-12 rounded-xl transition-all duration-200 group"
            >
              <UserPlus className="h-6 w-6"/>
              
              {/* Tooltip */}
              <div className="absolute left-16 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                Sign Up
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
              </div>
            </Button>
            
            <Button
              onClick={handleLogin}
              variant="ghost"
              size="icon"
              className="w-12 h-12 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-200 group"
            >
              <LogIn className="h-6 w-6"/>
              
              {/* Tooltip */}
              <div className="absolute left-16 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                {t('common.buttons.login')}
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
              </div>
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
};

const DesktopMenu = ({
  navigationItems,
  isAuthenticated,
  handleLogin,
  handleSignUp,
  handleLogout
}: {
  navigationItems: NavItem[];
  isAuthenticated: boolean;
  handleLogin: () => void;
  handleSignUp: () => void;
  handleLogout: () => void;
}) => {
  const { t } = useTranslation();
  const { userEmail, userName } = useAuth();

  return (
    <div className="flex items-center space-x-6">
      <nav className="flex items-center space-x-2">
        {navigationItems.map((item) => (
          <Button key={item.href} asChild variant="ghost">
            <Link to={item.href}>
              {item.icon}
              <span className="ml-2">{item.label}</span>
            </Link>
          </Button>
        ))}
      </nav>

      <Link to={'/itineraries/create'}>
              <Button variant="outline" className="w-full">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('common.buttons.cta')}
                </Button>
              </Link>


      {isAuthenticated ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="default"
              size="icon"
              className="relative rounded-full"
            >
              <User className="h-7 w-7"/>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <Link to="/profile">
              <DropdownMenuItem className="font-normal">
                <div className="flex flex-col space-y-1 w-full">
                  <p className="text-sm font-medium leading-none">
                    {userName}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground text-ellipsis overflow-hidden pb-1">
                    {userEmail}
                  </p>
                </div>
              </DropdownMenuItem>
            </Link>
            <DropdownMenuSeparator/>
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4"/>
              <span>{t('common.buttons.logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="flex items-center gap-2">
          <Button onClick={handleSignUp} variant="default">
            <UserPlus className="mr-2 h-4 w-4"/>
            <span>Sign Up</span>
          </Button>
          <Button onClick={handleLogin} variant="outline">
            <LogIn className="mr-2 h-4 w-4"/>
            <span>{t('common.buttons.login')}</span>
          </Button>
        </div>
      )}
      <LanguageSwitch />

    </div>
  );
};

// Main Navbar Component
export function Navbar() {
  const {instance, accounts} = useMsal();
  const isAuthenticated = accounts.length > 0;
  const { t } = useTranslation();
  const { signIn } = useAuth();

  const handleLogout = () => {
    instance
      .logoutRedirect()
      .catch((error) => console.log('Logout error:', error));
  };

  const handleLogin = () => {
    instance
      .loginRedirect()
      .catch((error) => console.log('Login error:', error));
  };

  const handleSignUp = () => {
    signIn();
  };

  const navigationItems = [
    {
      href: '/help',
      icon: <HelpCircle className="h-4 w-4" />,
      label: t('common.buttons.help')
    }
  ];

  return (
    <>
      {/* Mobile Top Navbar */}
      <MobileNavbar
        isAuthenticated={isAuthenticated}
        handleLogin={handleLogin}
        handleSignUp={handleSignUp}
        handleLogout={handleLogout}
      />

      {/* Mobile Bottom Navbar */}
      <MobileBottomNav />

      {/* Desktop Sidebar */}
      <DesktopSidebar
        isAuthenticated={isAuthenticated}
        handleLogin={handleLogin}
        handleSignUp={handleSignUp}
        handleLogout={handleLogout}
      />

      {/* Desktop Navbar */}
      <nav
        className={cn(
          "fixed top-0 left-16 right-0 z-50 bg-white border-b border-border hidden md:block",
        )}
      >
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex-1" /> {/* Spacer to push content to the right */}
            <DesktopMenu
              navigationItems={navigationItems}
              isAuthenticated={isAuthenticated}
              handleLogin={handleLogin}
              handleSignUp={handleSignUp}
              handleLogout={handleLogout}
            />
          </div>
        </div>
      </nav>
    </>
  );
}
